import { Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { useOidc } from '@nohara-dxdev/react-oidc';
import { useEffect, useState } from 'react';

import { checkPermission } from '@/utils/helpers';
import { GlobalSearch } from '@/components/search';
import { getSearchConfig } from '@/config/search-configs';
import { CompanyTable } from '@/features/companies/components';
import { PageTitle } from '@/components/styles';
import { useCompanies } from '@/features/companies/hooks/use-companies';
import { useCompanySearchStore } from '@/features/companies/stores/company-search.store';
import {
  PageContainer,
  PageHeader,
  TitleSection,
  ActionButtonContainer,
  CreateCompanyButton,
} from '@/features/companies/styles';
import { BusinessPartnerClassification, FunctionName, PermissionAction } from '@/lib/enums';
import { paths } from '@/config/paths';
import { Company } from '@/features/companies/types/table/company.types';

export const CompaniesPage = () => {
  const navigate = useNavigate();
  const {
    searchResults,
    isSearching,
    currentPage,
    itemsPerPage,
    totalItems,
    sortField,
    sortOrder,
    handleSortChange,
    handleSearch,
    handleSearchChange,
    handleClear,
    handlePageChange,
    handleItemsPerPageChange,
    handleRegisterAutocompleteClear,
  } = useCompanies();

  // Get search state from store
  const searchInputValues = useCompanySearchStore((state) => state.searchInputValues);
  const committedSearchParams = useCompanySearchStore((state) => state.committedSearchParams);
  const { permissions } = useOidc();
  const [canCreateCompany, setCanCreateCompany] = useState(false);
  const redirectToCreateCompany = () => {
    navigate('/companies/register');
  };

  const handleNavigateToCompanyDetail = (company: Company) => {
    const { id, partnerClassification } = company;

    switch (partnerClassification) {
      case BusinessPartnerClassification.CUSTOMER:
        navigate(paths.companiesRoutes.customer.getHref(id));
        break;
      case BusinessPartnerClassification.SUPPLIER:
        navigate(paths.companiesRoutes.supplier.getHref(id));
        break;
      case BusinessPartnerClassification.CONSTRUCTION_COMPANY:
        navigate(paths.companiesRoutes.constructionCompany.getHref(id));
        break;
      case BusinessPartnerClassification.WITNESS_COMPANY:
        navigate(paths.companiesRoutes.witnessCompany.getHref(id));
        break;
      default:
        console.warn('Unknown partner classification:', partnerClassification);
        break;
    }
  };

  const searchConfig = {
    ...getSearchConfig('companies'),
    initialValues: searchInputValues, // Pass initial values from store to restore form fields
    onSearch: (params: Record<string, unknown>) => {
      handleSearch({ ...params, sort: sortField, orderBy: sortOrder });
    },
    onChange: (params: Record<string, unknown>) => {
      handleSearchChange(params);
    },
    onClear: handleClear,
    loading: isSearching,
  };

  useEffect(() => {
    setCanCreateCompany(
      checkPermission(permissions, FunctionName.COMPANY_MANAGEMENT, PermissionAction.CREATE)
    );
  }, [permissions]);

  // Restore search when navigating back - trigger search if there are committed params
  useEffect(() => {
    // Trigger search if there are committed params (form fields are restored via initialValues)
    // Preserve page number when restoring
    if (Object.keys(committedSearchParams).length > 0) {
      // Set committed params and input values, but don't reset page
      // The useEffect in hook will trigger API call with current page from store
      handleSearch(committedSearchParams, true); // Preserve page number
    }
  }, []);

  return (
    <PageContainer>
      <PageHeader>
        <TitleSection>
          <PageTitle variant='h6'>会社一覧</PageTitle>
        </TitleSection>
        {canCreateCompany && (
          <ActionButtonContainer>
            <CreateCompanyButton
              variant='contained'
              startIcon={<AddIcon />}
              onClick={redirectToCreateCompany}
            >
              会社登録
            </CreateCompanyButton>
          </ActionButtonContainer>
        )}
      </PageHeader>
      <GlobalSearch config={searchConfig} clearAutocomplete={handleRegisterAutocompleteClear} />
      <CompanyTable
        data={searchResults}
        loading={isSearching}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        sortField={sortField}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        onCompanyClick={handleNavigateToCompanyDetail}
        key={`companies-table-${currentPage}-${JSON.stringify(committedSearchParams)}`}
      />
    </PageContainer>
  );
};

export default CompaniesPage;
